import {
  Avatar,
  lighten,
  LinkProps,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Skeleton,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';
import WorkspaceAPI from '../../../../redux/workspace/WorkspaceAPI';
import useWatchError from '../../../../shared/hooks/useWatchError';
import WorkspaceActions from './WorkspaceActions/WorkspaceActions';
import StructureAPI from '../../../../redux/structure/StructureAPI';
import useDeviceStatusFromWorkspace from '../../../../service/device/CommanderAPI/hooks/useDeviceStatusFromWorkspace';
import useAlarmsByWorkspace from '../../../../redux/alarm/hooks/useAlarmsByWorkspace';

interface WorkspaceItemProps {
  workspaceId: string;
  workspaceIdSelected?: string | null;
  /* Filter out the items that have alarms when the filter is active */
  isFilterTemperatureActive?: boolean;
  /* Filter out the items that have commander status failing when the filter is active */
  isFilterCommanderStatusFalling?: boolean;
}

function WorkspaceItem(props: WorkspaceItemProps) {
  const {
    workspaceId,
    workspaceIdSelected,
    isFilterCommanderStatusFalling,
    isFilterTemperatureActive,
  } = props;
  const {
    data: workspace,
    error,
    isLoading,
  } = WorkspaceAPI.useGetWorkspaceByIdQuery(workspaceId);

  // just fetch because I need the result later on the dashboard, and I want to have it in cache
  StructureAPI.useGetStructureV2Query(workspaceId, {
    skip: !workspaceId,
  });
  useWatchError(error);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const image = workspace?.images?.logo;
  const isSelected = workspaceId === workspaceIdSelected;
  const { data: alarmsData, isLoading: isLoadingAlarmsData } =
    useAlarmsByWorkspace(workspace?.id, {
      skip: !workspace?.id,
    });
  const hasAlarms = !!alarmsData?.length;
  const { numberFailingMqttConnection, isLoading: isLoadingDeviceStatus } =
    useDeviceStatusFromWorkspace(workspace?.id ?? '');
  const hasCommanderStatusFailing = !!numberFailingMqttConnection;

  const hide =
    (isFilterTemperatureActive && !hasAlarms && !isLoadingAlarmsData) ||
    (isFilterCommanderStatusFalling &&
      !hasCommanderStatusFailing &&
      !isLoadingDeviceStatus);

  if (isLoading) {
    return <WorkspaceItemSkeleton />;
  }

  if (hide) {
    return null;
  }

  return (
    <ListItem
      disablePadding
      secondaryAction={
        <WorkspaceActions {...props} workspaceId={workspace?.id} />
      }
    >
      <StyledListItemButton
        selected={isSelected}
        href={`${workspaceId}/view-locations`}
        bgcolor={workspace?.settings?.brandColor}
        sx={{
          ml: 2,
          '& span': {
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            maxWidth: 'calc(100% - 310px)',
          },
          '& .MuiListItemText-secondary': {
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          },
        }}
      >
        <ListItemAvatar>
          <Avatar
            src={image}
            alt={workspace?.name}
            variant="square"
            sx={{
              width: 'auto',
              height: '2.5rem',
              padding: '0.5rem',
              margin: '0 0.25rem 0 0',
            }}
          >
            {!image && workspace?.name.charAt(0).toUpperCase()}
          </Avatar>
        </ListItemAvatar>
        {isMobile && workspaceIdSelected ? null : (
          <ListItemText primary={workspace?.name} />
        )}
      </StyledListItemButton>
    </ListItem>
  );
}

export const WorkspaceItemSkeleton = () => {
  return (
    <ListItem disablePadding>
      <StyledListItemButton>
        <ListItemAvatar>
          <Skeleton variant="rectangular" width={40} height={40} />
        </ListItemAvatar>
        <ListItemText>
          <Skeleton variant="text" width="60%" />
        </ListItemText>
      </StyledListItemButton>
    </ListItem>
  );
};

export const StyledListItemButton = styled(ListItemButton)<
  LinkProps & {
    selected?: boolean;
    bgcolor?: string;
  }
>(({ theme, selected, bgcolor }) => ({
  backgroundColor: selected
    ? lighten(theme.palette.primary.main, 0.85)
    : lighten(theme.palette.background.paper, 0.1),
  borderLeft: bgcolor
    ? `4px solid ${bgcolor}`
    : selected
    ? `4px solid ${theme.palette.primary.main}`
    : `4px solid ${theme.palette.text.primary}`,
  color: theme.palette.text.primary,
  my: 0.5,
  display: 'flex',
  justifyContent: 'space-between',
  '&:hover': {
    backgroundColor: selected
      ? lighten(theme.palette.primary.main, 0.75)
      : lighten(theme.palette.background.paper, 0.2),
  },
}));

export default WorkspaceItem;
